/**
 * Competitor stack detector.
 *
 * Fingerprints the UGC / reviews / shoppable-video apps a store runs,
 * plus the ecommerce platform underneath, from a fetched HTML body.
 * Deterministic: regex signatures against the served markup. No AI.
 *
 * Broader than the Shopify theme detector: it works on any platform
 * (Shopify, WooCommerce, BigCommerce, Magento, Wix, Squarespace,
 * Salesforce Commerce, custom) and groups detected apps by category so
 * a merchant can see their whole social-proof stack at a glance.
 *
 * Pure string/regex matching, runs anywhere (browser or edge). No DOM.
 */

export type StackCategory =
  | 'reviews'
  | 'ugc-gallery'
  | 'shoppable-video'
  | 'live-shopping'
  | 'social-wall'
  | 'personalization'
  | 'email-sms'
  | 'helpdesk'
  | 'upsell'
  | 'page-builder'

export type Signature = {
  name: string
  category: StackCategory
  rx: RegExp
  /** true when this is Idukki itself (special-cased in the UI). */
  isIdukki?: boolean
}

export type DetectedApp = {
  name: string
  category: StackCategory
}

export type PlatformId =
  | 'shopify'
  | 'woocommerce'
  | 'bigcommerce'
  | 'magento'
  | 'wix'
  | 'squarespace'
  | 'salesforce-commerce'
  | 'prestashop'
  | 'webflow'
  | 'unknown'

export type StackResult = {
  platform: PlatformId
  platformLabel: string
  apps: DetectedApp[]
  isIdukkiCustomer: boolean
  /** Distinct categories present, in display order. */
  categories: StackCategory[]
  signals: string[]
}

export const CATEGORY_LABELS: Record<StackCategory, string> = {
  reviews: 'Reviews',
  'ugc-gallery': 'UGC galleries',
  'shoppable-video': 'Shoppable video',
  'live-shopping': 'Live shopping',
  'social-wall': 'Social walls',
  personalization: 'Personalization',
  'email-sms': 'Email + SMS',
  helpdesk: 'Support desk',
  upsell: 'Upsells',
  'page-builder': 'Page builders',
}

const CATEGORY_ORDER: StackCategory[] = [
  'ugc-gallery',
  'shoppable-video',
  'live-shopping',
  'reviews',
  'social-wall',
  'personalization',
  'email-sms',
  'upsell',
  'helpdesk',
  'page-builder',
]

// Detection order matters: Idukki / Feefo run first so the store is
// recognised as ours before competitors are listed.
export const SIGNATURES: Signature[] = [
  // ── Idukki (+ Feefo white-label) ──
  {
    name: 'Idukki',
    category: 'ugc-gallery',
    isIdukki: true,
    rx: /data-ugc=["']idukki["']|idukki-cdn\.com|widget-api\.idukki\.io|widget\.idukki\.io|widget-staging\.idukki\.io|app\.idukki\.io|assets\.idukki\.io|idukki\.io\/(?:widget|embed)|\bidukki[._-]?(?:js|widget|loader|runtime)\b/i,
  },
  { name: 'Feefo (Idukki white-label)', category: 'reviews', isIdukki: true, rx: /<feefo-ugc|api\.feefo\.com|cdn\.feefo\.com|widget\.feefo\.com/i },

  // ── Reviews ──
  { name: 'Yotpo', category: 'reviews', rx: /yotpo|staticw2\.yotpo\.com/i },
  { name: 'Okendo', category: 'reviews', rx: /okendo|oke-w|oke-widget/i },
  { name: 'Loox', category: 'reviews', rx: /loox\.io|cdn\.loox|loox-product-reviews|data-loox/i },
  { name: 'Judge.me', category: 'reviews', rx: /judge\.me|jdgm-/i },
  { name: 'Stamped', category: 'reviews', rx: /stamped\.io|stamped-io/i },
  { name: 'Reviews.io', category: 'reviews', rx: /reviews\.io|cdn\.reviews\.co\.uk|reviewsio/i },
  { name: 'Trustpilot', category: 'reviews', rx: /trustpilot\.com\/widgets|widget\.trustpilot|trustbox/i },
  { name: 'Bazaarvoice', category: 'reviews', rx: /bazaarvoice|apps\.bazaarvoice|bv-widget/i },
  { name: 'Junip', category: 'reviews', rx: /\bjunip\b|junip\.co/i },
  { name: 'Opinew', category: 'reviews', rx: /opinew/i },
  { name: 'Fera', category: 'reviews', rx: /fera\.ai|frb\.ai/i },
  { name: 'Shopper Approved', category: 'reviews', rx: /shopperapproved/i },
  { name: 'Google Reviews', category: 'reviews', rx: /shopper-rating-snippet|google-reviews/i },

  // ── UGC galleries / social walls ──
  { name: 'Flowbox', category: 'ugc-gallery', rx: /getflowbox|flowbox/i },
  { name: 'Storista', category: 'ugc-gallery', rx: /storista|stori\.sta/i },
  { name: 'Stackla / Nosto UGC', category: 'ugc-gallery', rx: /stackla|nosto-stackla|teaser\.stackla/i },
  { name: 'Olapic', category: 'ugc-gallery', rx: /olapic/i },
  { name: 'Pixlee TurnTo', category: 'ugc-gallery', rx: /pixlee|pixleeturnto/i },
  { name: 'Foursixty', category: 'ugc-gallery', rx: /foursixty|fs-entry-/i },
  { name: 'Candid', category: 'ugc-gallery', rx: /candid\.io|candidwm/i },
  { name: 'Tagshop', category: 'ugc-gallery', rx: /tagshop|tagsh\.op/i },
  { name: 'EmbedSocial', category: 'social-wall', rx: /embedsocial|embed\.social|embedstories/i },
  { name: 'Taggbox', category: 'social-wall', rx: /taggbox/i },
  { name: 'TINT', category: 'social-wall', rx: /\btintup\b|tint\.io/i },
  { name: 'Curator.io', category: 'social-wall', rx: /curator\.io/i },

  // ── Shoppable video ──
  { name: 'Tolstoy', category: 'shoppable-video', rx: /tolstoy|gotolstoy/i },
  { name: 'Videowise', category: 'shoppable-video', rx: /videowise|vw-widget/i },
  { name: 'Reelup', category: 'shoppable-video', rx: /reelup/i },
  { name: 'Whatmore', category: 'shoppable-video', rx: /whatmore/i },
  { name: 'Firework', category: 'shoppable-video', rx: /firework\.com|fwn\.cc/i },
  { name: 'Vidjet', category: 'shoppable-video', rx: /vidjet/i },
  { name: 'Smartzer', category: 'shoppable-video', rx: /smartzer/i },
  { name: 'Quinn', category: 'shoppable-video', rx: /quinnvideo|quinn\.live/i },
  { name: 'Videeo', category: 'shoppable-video', rx: /videeo\.live/i },

  // ── Live shopping ──
  { name: 'Bambuser', category: 'live-shopping', rx: /bambuser/i },
  { name: 'CommentSold', category: 'live-shopping', rx: /commentsold/i },
  { name: 'Channelize', category: 'live-shopping', rx: /channelize/i },

  // ── Adjacent stack (so the read looks like a full social-proof stack) ──
  { name: 'Nosto', category: 'personalization', rx: /nostojs|nosto\.com/i },
  { name: 'Klaviyo', category: 'email-sms', rx: /klaviyo/i },
  { name: 'Attentive', category: 'email-sms', rx: /attentive/i },
  { name: 'Postscript', category: 'email-sms', rx: /postscript/i },
  { name: 'Gorgias', category: 'helpdesk', rx: /gorgias/i },
  { name: 'Rebuy', category: 'upsell', rx: /rebuyengine|rebuy/i },
  { name: 'Shogun', category: 'page-builder', rx: /getshogun|shogun/i },
  { name: 'PageFly', category: 'page-builder', rx: /pagefly/i },
]

const PLATFORM_SIGS: { id: PlatformId; label: string; rx: RegExp }[] = [
  { id: 'shopify', label: 'Shopify', rx: /cdn\.shopify\.com|Shopify\.theme\s*=|shopify-features|myshopify\.com|x-shopify/i },
  { id: 'woocommerce', label: 'WooCommerce', rx: /woocommerce|wp-content\/plugins\/woocommerce|wc-block/i },
  { id: 'bigcommerce', label: 'BigCommerce', rx: /bigcommerce|cdn11\.bigcommerce\.com|stencil-utils/i },
  { id: 'magento', label: 'Magento / Adobe Commerce', rx: /magento|mage\/|static\/version\d|Magento_/i },
  { id: 'salesforce-commerce', label: 'Salesforce Commerce', rx: /demandware|dwstatic|sfcc/i },
  { id: 'prestashop', label: 'PrestaShop', rx: /prestashop/i },
  { id: 'wix', label: 'Wix', rx: /wix\.com|wixstatic|parastorage/i },
  { id: 'squarespace', label: 'Squarespace', rx: /squarespace|sqsp\.net|static1\.squarespace/i },
  { id: 'webflow', label: 'Webflow', rx: /webflow|wf-|assets-global\.website-files/i },
]

function detectPlatform(html: string): { id: PlatformId; label: string } {
  for (const p of PLATFORM_SIGS) {
    if (p.rx.test(html)) return { id: p.id, label: p.label }
  }
  return { id: 'unknown', label: 'Custom / unknown platform' }
}

export function detectStack(html: string): StackResult {
  const platform = detectPlatform(html)
  const apps: DetectedApp[] = []
  const seen = new Set<string>()
  let isIdukkiCustomer = false

  for (const sig of SIGNATURES) {
    if (seen.has(sig.name)) continue
    if (sig.rx.test(html)) {
      seen.add(sig.name)
      apps.push({ name: sig.name, category: sig.category })
      if (sig.isIdukki) isIdukkiCustomer = true
    }
  }

  const present = new Set(apps.map((a) => a.category))
  const categories = CATEGORY_ORDER.filter((c) => present.has(c))

  const signals: string[] = []
  if (platform.id !== 'unknown') signals.push(`Platform fingerprint: ${platform.label}.`)
  else signals.push('No platform fingerprint matched. The store may be fully custom or behind a CDN that strips markers.')
  const ugcCount = apps.filter((a) => ['ugc-gallery', 'shoppable-video', 'live-shopping', 'social-wall'].includes(a.category)).length
  const reviewCount = apps.filter((a) => a.category === 'reviews').length
  if (ugcCount > 0) signals.push(`${ugcCount} UGC / video app(s) detected.`)
  if (reviewCount > 0) signals.push(`${reviewCount} reviews app(s) detected.`)
  if (isIdukkiCustomer) signals.push('Idukki widget detected, this is already an Idukki customer.')
  if (apps.length === 0) signals.push('No UGC, reviews or video apps detected in the served HTML.')

  return {
    platform: platform.id,
    platformLabel: platform.label,
    apps,
    isIdukkiCustomer,
    categories,
    signals,
  }
}
